import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import WebsiteNavbar from './WebsiteNavbar';
import WebsiteFooter from './WebsiteFooter';
import './Website.css';

const SECTORS = [
  { n: '01', name: 'Banking & Finance', tag: 'Commercial banks, fintechs and microfinance' },
  { n: '02', name: 'Telecommunications', tag: 'GSM operators and internet service providers' },
  { n: '03', name: 'E-commerce', tag: 'Online retail, marketplaces and delivery' },
  { n: '04', name: 'Insurance & HMO', tag: 'General insurance and health management' },
  { n: '05', name: 'Transportation', tag: 'Aviation, intra-city and inter-state travel' },
  { n: '06', name: 'Healthcare', tag: 'Private facilities and public institutions' },
  { n: '07', name: 'Public Sector', tag: 'MDAs and foreign missions' },
  { n: '08', name: 'Hospitality', tag: 'Hotels, resorts, eateries and leisure' },
  { n: '09', name: 'Power', tag: 'Electricity distribution companies' },
  { n: '10', name: 'Real Estate', tag: 'Residential and commercial housing' },
  { n: '11', name: 'Education', tag: 'Private and public universities' },
  { n: '12', name: 'Sports Gaming & Entertainment', tag: 'Betting and gaming organizations' },
];

const PARAMETERS = [
  { n: '01', t: 'Reliability', d: 'Consistent delivery on promises, every time.' },
  { n: '02', t: 'Responsiveness', d: 'Speed and willingness to help when it matters.' },
  { n: '03', t: 'Empathy', d: 'Courtesy, understanding and respect for the customer.' },
  { n: '04', t: 'Assurance', d: 'Competence and the confidence it inspires.' },
  { n: '05', t: 'Tangibles', d: 'Physical spaces, digital channels and presentation.' },
  { n: '06', t: 'Transparency', d: 'Clarity of pricing, terms and communication.' },
  { n: '07', t: 'Value for money', d: 'Quality received relative to cost.' },
  { n: '08', t: 'Resolution', d: 'Effectiveness of complaint handling and recovery.' },
];

const SECTOR_SCORES = [
  ['Telecommunications', 74], ['Banking & Finance', 71], ['E-commerce', 69], ['Power', 41],
];

const TREND = [
  ['Q4', 64.1], ['Q1', 65.8], ['Q2', 66.3], ['Q3', 68.4],
];

const PARAM_SCORES = [
  ['Reliability', 72], ['Responsiveness', 70], ['Empathy', 69],
  ['Transparency', 66], ['Value for money', 63], ['Resolution', 58],
];

const CHART_VIEWS = ['Sectors', 'Trend', 'Parameters'];

const NxIndexCharts = () => {
  const [view, setView] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setView((v) => (v + 1) % CHART_VIEWS.length), 5000);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <aside
      className="nx-index-panel"
      aria-label="National index charts"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="nx-panel-head">
        <span>National Composite Q3</span>
        <span className="nx-status">Collecting responses</span>
      </div>
      <div className="nx-panel-score">
        <div className="nx-score">68.4</div>
        <div className="nx-score-sub">out of 100 · <b>+2.1</b> vs last quarter</div>
      </div>

      <div className="nx-chart-tabs" role="tablist" aria-label="Chart views">
        {CHART_VIEWS.map((label, i) => (
          <button
            key={label}
            role="tab"
            aria-selected={view === i}
            className={view === i ? 'active' : ''}
            onClick={() => setView(i)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      <div className="nx-chart-stage">
        {view === 0 && (
          <div key="sectors" className="nx-chart-view">
            {SECTOR_SCORES.map(([label, v], i) => (
              <div className="nx-pbar" key={label}>
                <div className="nx-pbar-label"><span>{label}</span><b>{v}</b></div>
                <div className="nx-pbar-track">
                  <i style={{ width: `${v}%`, animationDelay: `${i * 110}ms` }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {view === 1 && (
          <div key="trend" className="nx-chart-view">
            <svg className="nx-trend" viewBox="0 0 300 150" role="img" aria-label="Index trend rising from 64.1 to 68.4">
              {[30, 60, 90, 120].map((y) => (
                <line key={y} x1="8" y1={y} x2="292" y2={y} className="nx-trend-grid" />
              ))}
              <polygon
                points="20,71 100,50 180,44 260,18 260,132 20,132"
                fill="#14382a"
                fillOpacity="0.12"
                className="nx-trend-area"
              />
              <polyline
                points="20,71 100,50 180,44 260,18"
                fill="none"
                pathLength="100"
                className="nx-trend-line"
              />
              {[[20, 71, '64.1'], [100, 50, '65.8'], [180, 44, '66.3'], [260, 18, '68.4']].map(([x, y, v], i) => (
                <g key={v} className="nx-trend-dot" style={{ animationDelay: `${300 + i * 160}ms` }}>
                  <circle cx={x} cy={y} r="5" />
                  <text x={x} y={y - 12} textAnchor="middle">{v}</text>
                </g>
              ))}
              {[['Q4', 20], ['Q1', 100], ['Q2', 180], ['Q3', 260]].map(([q, x]) => (
                <text key={q} x={x} y="146" textAnchor="middle" className="nx-trend-q">{q}</text>
              ))}
            </svg>
          </div>
        )}

        {view === 2 && (
          <div key="params" className="nx-chart-view">
            {PARAM_SCORES.map(([label, v], i) => (
              <div className="nx-pbar" key={label}>
                <div className="nx-pbar-label"><span>{label}</span><b>{v}</b></div>
                <div className="nx-pbar-track">
                  <i style={{ width: `${v}%`, animationDelay: `${i * 80}ms` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="nx-chart-dots" aria-hidden="true">
        {CHART_VIEWS.map((label, i) => (
          <span key={label} className={view === i ? 'active' : ''} />
        ))}
      </div>

      <Link to="/survey/" className="nx-panel-cta">Add your assessment</Link>
      <div className="nx-panel-foot">Anonymous · ~3 minutes · Citizen-powered</div>
    </aside>
  );
};

const STEPS = [
  { n: '01', t: 'Select your sector', d: 'Twelve sectors, fully broken down by sub-sector, from banks to betting platforms.', cta: 'View sectors', to: '/sectors' },
  { n: '02', t: 'Complete the assessment', d: 'A focused survey across eight service parameters. About three minutes.', cta: 'See parameters', to: '/about' },
  { n: '03', t: 'Shape the national index', d: 'Responses are collated into the independent report used by institutions and regulators.', cta: 'Start now', to: '/survey/' },
];

const WebsiteHome = () => {
  return (
    <div className="website nx nx-refined">
      <WebsiteNavbar />

      <div className="nx-rule" aria-hidden="true">
        <span>Nigeria Customer Service Index</span>
        <span>Independent · Objective · National</span>
      </div>

      {/* HERO */}
      <header
        className="nx-hero nx-hero-image"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/hero-bg.jpg)` }}
      >
        <div className="nx-hero-shade" aria-hidden="true" />
        <div className="nx-hero-inner">
          <div className="nx-hero-copy">
            <div className="nx-eyebrow">
              The national barometer for service delivery
            </div>
            <h1>
              How Nigeria experiences service.
            </h1>
            <p className="nx-lede">
              The Nigeria Customer Service Index gathers citizen assessments
              across twelve sectors, producing the independent evidence base
              that institutions, regulators and the public rely on.
            </p>
            <div className="nx-hero-btns">
              <Link to="/survey/" className="nx-btn nx-btn-primary">
                Participate in the survey
              </Link>
              <Link to="/sectors" className="nx-btn nx-btn-secondary">
                Explore the sectors
              </Link>
            </div>
            <div className="nx-hero-meta">
              <div className="nx-meta-item"><strong>12</strong><span>Sectors</span></div>
              <div className="nx-meta-divider" aria-hidden="true" />
              <div className="nx-meta-item"><strong>21</strong><span>Sub-sectors</span></div>
              <div className="nx-meta-divider" aria-hidden="true" />
              <div className="nx-meta-item"><strong>8</strong><span>Parameters</span></div>
            </div>
          </div>

          <NxIndexCharts />
        </div>
      </header>

      {/* SECTORS */}
      <section className="nx-sectors">
        <div className="nx-section-head">
          <div>
            <div className="nx-kicker">Coverage</div>
            <h2>Twelve sectors, one national picture.</h2>
          </div>
          <div className="nx-head-side">
            <p>Select the sector where you received service and submit a structured assessment.</p>
            <Link to="/sectors" className="nx-link">View sector detail</Link>
          </div>
        </div>
        <div className="nx-sector-list">
          {SECTORS.map((s) => (
            <Link to="/survey/" key={s.n} className="nx-sector-row">
              <span className="nx-sector-n">{s.n}</span>
              <span className="nx-sector-body">
                <h3>{s.name}</h3>
                <p>{s.tag}</p>
              </span>
              <span className="nx-sector-arrow" aria-hidden="true">→</span>
            </Link>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="nx-steps">
        <div className="nx-steps-inner">
          <div className="nx-kicker nx-kicker-muted">Method</div>
          <h2>From experience to evidence.</h2>
          <div className="nx-steps-grid">
            {STEPS.map((s) => (
              <div className="nx-step" key={s.n}>
                <div className="nx-step-n">{s.n}</div>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
                <Link to={s.to} className="nx-step-link">{s.cta}</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PARAMETERS */}
      <section className="nx-params">
        <div className="nx-section-head">
          <div>
            <div className="nx-kicker">Framework</div>
            <h2>Assessed on eight parameters.</h2>
          </div>
          <p className="nx-head-side-p">
            A consistent framework applied across every sector, enabling
            fair comparison and longitudinal tracking.
          </p>
        </div>
        <div className="nx-params-grid">
          {PARAMETERS.map((p) => (
            <div className="nx-param" key={p.n}>
              <span className="nx-param-n">{p.n}</span>
              <h3>{p.t}</h3>
              <p>{p.d}</p>
            </div>
          ))}
        </div>
        <div className="nx-params-cta">
          <Link to="/survey/" className="nx-btn nx-btn-primary">Begin assessment</Link>
        </div>
      </section>

      {/* ABOUT */}
      <section className="nx-manifesto">
        <div className="nx-mani-grid">
          <div className="nx-mani-copy">
            <div className="nx-kicker">About the index</div>
            <h2>An independent record of service in Nigeria.</h2>
            <p>
              The Nigeria Customer Service Index is an objective, nationwide
              compilation of customer satisfaction across goods and service
              delivery, revealing trends, causes and effects shaping
              national life.
            </p>
            <ul className="nx-checks">
              <li>Benchmark performance against sector standards</li>
              <li>Track consumer behaviour and emerging trends</li>
              <li>Inform planning with citizen-driven evidence</li>
            </ul>
            <div className="nx-hero-btns" style={{ justifyContent: 'flex-start' }}>
              <Link to="/survey/" className="nx-btn nx-btn-primary">Take the survey</Link>
              <Link to="/contact" className="nx-btn nx-btn-secondary">Contact the team</Link>
            </div>
          </div>
          <div className="nx-mani-figure">
            <img src={process.env.PUBLIC_URL + '/images/about-visual.png'} alt="NCSI sector overview" />
            <div className="nx-mani-caption">
              <strong>Objective · National · Citizen-first</strong>
              <span>Compiled in association with research partners</span>
            </div>
          </div>
        </div>
      </section>

      {/* VOICES */}
      <section className="nx-voices">
        <div className="nx-kicker">Perspectives</div>
        <h2>Why respondents participate.</h2>
        <div className="nx-voices-grid">
          <figure className="nx-voice">
            <blockquote>“A structured way to hold my bank to account, beyond a complaint that goes nowhere.”</blockquote>
            <figcaption><b>Adaeze O.</b><span>Lagos · Banking</span></figcaption>
          </figure>
          <figure className="nx-voice">
            <blockquote>“The report gave our management evidence we could act on. Claims handling improved.”</blockquote>
            <figcaption><b>Ibrahim M.</b><span>Kano · Healthcare</span></figcaption>
          </figure>
          <figure className="nx-voice">
            <blockquote>“For customer experience planning, the benchmarks and trends are indispensable.”</blockquote>
            <figcaption><b>Funmi A.</b><span>Port Harcourt · Hospitality</span></figcaption>
          </figure>
        </div>
      </section>

      {/* REPORTS */}
      <section className="nx-reports">
        <div className="nx-section-head">
          <div>
            <div className="nx-kicker">Reports</div>
            <h2>Download the published index.</h2>
          </div>
          <p className="nx-head-side-p">
            Full national reports with sector scores, trends and analysis.
          </p>
        </div>
        <div className="nx-reports-grid">
          <div className="nx-report">
            <span className="nx-report-year">2024</span>
            <h3>NCSI National Report 2024</h3>
            <p>Sector rankings and year on year movement across all indexed sectors.</p>
            <a
              href="https://drive.google.com/file/d/1S7KQ0coEeTzEsViTd-fNrJXpvVSz716m/view"
              target="_blank"
              rel="noopener noreferrer"
              className="nx-btn nx-btn-primary"
            >
              Download PDF
            </a>
          </div>
          <div className="nx-report">
            <span className="nx-report-year">2023</span>
            <h3>NCSI National Report 2023</h3>
            <p>The baseline edition that started the national conversation on service.</p>
            <a
              href="https://drive.google.com/file/d/1363fILTmx4vR84MrjKAIoVw_Dlrocccp/view"
              target="_blank"
              rel="noopener noreferrer"
              className="nx-btn nx-btn-primary"
            >
              Download PDF
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="nx-cta">
        <div className="nx-cta-inner">
          <div className="nx-kicker nx-kicker-muted">Participate</div>
          <h2>Your experience belongs in the national record.</h2>
          <p>Three minutes. Anonymous. Used by institutions across the country.</p>
          <div className="nx-hero-btns">
            <Link to="/survey/" className="nx-btn nx-btn-gold">Rate a sector</Link>
            <a href="https://thewaacsp.com" target="_blank" rel="noopener noreferrer" className="nx-btn nx-btn-outline-light">WAACSP membership</a>
          </div>
        </div>
      </section>

      <WebsiteFooter />
    </div>
  );
};

export default WebsiteHome;
